import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Loader2, Lightbulb, Flag, Search, Mic, Eye, Layers, Users, Heart, Wand2, ChevronDown, ArrowUpRight,
} from "lucide-react";

const ICONS = { flag: Flag, search: Search, mic: Mic, eye: Eye, layers: Layers, users: Users, heart: Heart, wand: Wand2 };

export default function WritingTips() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    api.get("/tips")
      .then(({ data }) => { setTips(data); setOpen(data[0]?.id || null); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  if (error) return (
    <div className="max-w-md mx-auto text-center py-20" data-testid="tips-error">
      <p className="text-subtle">We couldn't load the writing tips just now.</p>
      <button onClick={load} className="mt-4 bg-teal text-cream px-6 py-2.5 rounded-full font-semibold">Try again</button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center shrink-0"><Lightbulb size={24} /></span>
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Juliet's Memoir Guidance</h1>
          <p className="text-subtle mt-1">Gentle, practical tips from Juliet Sullivan — author, journalist and memoir writer — for turning your memories into a book. Dip in whenever you need a little help.</p>
        </div>
      </div>

      <div className="space-y-3">
        {tips.map((cat, i) => {
          const Icon = ICONS[cat.icon] || Lightbulb;
          const isOpen = open === cat.id;
          return (
            <div key={cat.id} data-testid={`tip-category-${cat.id}`} className="bg-white rounded-2xl border border-line overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <button
                data-testid={`tip-toggle-${cat.id}`}
                onClick={() => setOpen(isOpen ? null : cat.id)}
                className="w-full flex items-center gap-4 p-5 text-left hover:bg-cream2/50 transition-colors"
              >
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isOpen ? "bg-teal text-cream" : "bg-sagelight text-teal"}`}>
                  <Icon size={20} />
                </span>
                <span className="flex-1">
                  <span className="font-serif text-lg text-charcoal block">{cat.title}</span>
                  {!isOpen && <span className="text-sm text-subtle line-clamp-1">{cat.intro}</span>}
                </span>
                <ChevronDown size={20} className={`text-subtle shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>

              {isOpen && (
                <div className="px-5 pb-6 pt-1 animate-fade-in">
                  <p className="text-charcoal/80 italic border-l-2 border-sage pl-4 mb-5">{cat.intro}</p>
                  <div className="space-y-4">
                    {cat.tips.map((t, j) => (
                      <div key={t.title} data-testid={`tip-${cat.id}-${j}`} className="flex gap-3">
                        <span className="font-serif text-teal text-lg leading-none mt-0.5">{j + 1}.</span>
                        <div>
                          <p className="font-semibold text-charcoal">{t.title}</p>
                          <p className="text-subtle leading-relaxed mt-0.5">{t.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div data-testid="juliet-consultation-card" className="rounded-3xl bg-teal text-cream p-7 sm:p-8 animate-fade-up">
        <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold">From Juliet</p>
        <h2 className="font-serif text-2xl mt-2">Would you like a little more help?</h2>
        <p className="text-cream/85 mt-2 leading-relaxed max-w-xl">
          Sometimes it helps to talk to someone who does this for a living. Juliet Sullivan offers one-to-one
          memoir consultations — a warm, practical conversation about your story and how best to tell it.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <a data-testid="consultation-btn" href="https://www.julietsullivan.co.uk/writing-services/consultation/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-6 h-12 rounded-full font-semibold transition-colors">
            Need professional guidance? <ArrowUpRight size={18} />
          </a>
          <a data-testid="juliet-website-link" href="https://www.julietsullivan.co.uk" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-cream/10 hover:bg-cream/20 border border-cream/30 text-cream px-6 h-12 rounded-full font-semibold transition-colors">
            Visit Juliet's website <ArrowUpRight size={18} />
          </a>
        </div>
      </div>
    </div>
  );
}
